import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface RevenueSummaryResponse {
  period: string;
  from: string;
  to: string;
  gross_revenue_ils_agorot: number;
  net_revenue_ils_agorot: number;
  vat_collected_ils_agorot: number;
  trailing_30d_ils_agorot: number;
  summary_text: string;
}

interface ExpensesResponse {
  expenses: unknown[];
  total: number;
}

interface ReportsResponse {
  reports: { status: string }[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data: summaryData } = useQuery({
    queryKey: ['finance', 'revenue', 'summary', 'monthly'],
    queryFn: () => apiFetch<RevenueSummaryResponse>('/finance/revenue/summary?period=monthly'),
    refetchInterval: 30_000,
  });

  const { data: expensesData } = useQuery({
    queryKey: ['finance', 'expenses'],
    queryFn: () => apiFetch<ExpensesResponse>('/finance/expenses'),
    refetchInterval: 30_000,
  });

  const { data: reportsData } = useQuery({
    queryKey: ['finance', 'reports'],
    queryFn: () => apiFetch<ReportsResponse>('/finance/reports'),
    refetchInterval: 30_000,
  });

  const pendingReports = reportsData?.reports?.filter(r => r.status === 'pending').length ?? 0;

  const navLinks = [
    { to: '/omega/finance/revenue',  label: 'Revenue' },
    { to: '/omega/finance/expenses', label: 'Expenses' },
    { to: '/omega/finance/reports',  label: 'Reports' },
    { to: '/omega/finance/tax',      label: 'Taxes' },
    { to: '/omega/finance/pl',       label: 'CPA' },
  ];

  const navLinkStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Finance' }]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Finance Brain
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Financial management
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard
          label="Gross revenue this month"
          value={summaryData ? fmtIls(summaryData.gross_revenue_ils_agorot) : '—'}
          highlight
        />
        <StatCard
          label="Revenue — last 30 days"
          value={summaryData ? fmtIls(summaryData.trailing_30d_ils_agorot) : '—'}
        />
        <StatCard
          label="Pending reports"
          value={reportsData ? pendingReports : '—'}
          color={pendingReports > 0 ? '#fbbf24' : 'var(--omega-ink)'}
          highlight={pendingReports > 0}
        />
        <StatCard
          label="Expenses"
          value={expensesData ? (expensesData.total) : '—'}
        />
      </div>

      {/* R2-3d: explain the revenue figure (especially $0) in plain language */}
      {summaryData?.summary_text && (
        <p
          style={{
            fontSize: '10pt',
            color: 'var(--omega-ink-muted)',
            marginTop: -14,
            marginBottom: 28,
          }}
        >
          {summaryData.summary_text}
        </p>
      )}

      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Tools
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, maxWidth: 600 }}>
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} style={navLinkStyle}>
              {label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
